import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Popover, Spin, Tooltip } from "antd";
import { AppstoreOutlined } from "@ant-design/icons";
import Service from "../../service";
import { THEME_TYPE_LITE } from "../../constants/ThemeSetting";
import "./LinkedProductsLauncher.css";

// Connected-products button + product logos.
// The logos are the SAME assets used in the Registration welcome email
// (weekmateregistration/server/template/email-assets/*.svg) and the launcher is an icon tile. Logos are mirrored into every WeekMate product so the
// feature looks identical across HRMS / Payroll / CRM / TaskHub / Econnect.
import HrmsLogo from "./connectedProductLogos/hrms.svg";
import CrmLogo from "./connectedProductLogos/crm.svg";
import TaskHubLogo from "./connectedProductLogos/taskhub.svg";
import EconnectLogo from "./connectedProductLogos/econnect.svg";
import PayrollLogo from "./connectedProductLogos/payroll.svg";

const PRODUCT_LOGO_MAP = {
  "hrms": HrmsLogo,
  "e-hrms": HrmsLogo,
  "ehrms": HrmsLogo,
  "crm": CrmLogo,
  "ecrm": CrmLogo,
  "e-crm": CrmLogo,
  "taskhub": TaskHubLogo,
  "task hub": TaskHubLogo,
  "etaskhub": TaskHubLogo,
  "e-task hub": TaskHubLogo,
  "econnect": EconnectLogo,
  "e-connect": EconnectLogo,
  "payroll": PayrollLogo,
  "pay roll": PayrollLogo,
  "pms": PayrollLogo,
};

function getProductLogo(productName) {
  if (!productName || typeof productName !== "string") return null;
  const key = productName.trim().toLowerCase().replace(/\s+/g, " ");
  return PRODUCT_LOGO_MAP[key] || PRODUCT_LOGO_MAP[key.replace(/\s/g, "")] || null;
}

// White modal, sky-blue tiles
const LIGHT_THEME = {
  popoverBg: "#ffffff",
  popoverBorder: "none",
  cardBg: "#F7FAFD",
  cardBorder: "1px solid #E6EDF5",
  labelColor: "#24323f",
  emptyColor: "#666",
};
const DARK_THEME = {
  popoverBg: "#ffffff",
  popoverBorder: "none",
  cardBg: "#F7FAFD",
  cardBorder: "1px solid #E6EDF5",
  labelColor: "#24323f",
  emptyColor: "#666",
};

const LinkedProductsLauncher = () => {
  const themeType = useSelector(({ settings }) => settings.themeType);
  const isLightTheme = themeType === THEME_TYPE_LITE;
  const theme = isLightTheme ? LIGHT_THEME : DARK_THEME;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  const themeVars = {
    "--lp-popover-bg": theme.popoverBg,
    "--lp-card-bg": theme.cardBg,
    "--lp-card-border": theme.cardBorder,
    "--lp-label": theme.labelColor,
    "--lp-empty": theme.emptyColor,
  };

  const fetchLinkedProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Service.makeAPICall({
        methodName: Service.getMethod,
        api_url: Service.linkedProducts,
      });
      const data = response?.data?.data;
      setList(Array.isArray(data) ? data : []);
    } catch (err) {
      setList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount so the button can be hidden entirely when the company has no other
  // connected products (e.g. only HRMS selected in Registration).
  useEffect(() => {
    fetchLinkedProducts();
  }, [fetchLinkedProducts]);

  const handleOpenChange = useCallback((open) => {
    if (open) fetchLinkedProducts();
  }, [fetchLinkedProducts]);

  const ssoToken = (() => {
    const ls = localStorage.getItem("ssoToken");
    if (ls) return ls;
    const m = document.cookie.match(/(?:^|;\s*)wm_shared_token=([^;]*)/);
    return m ? decodeURIComponent(m[1]) : "";
  })() || "";

  const content = (
    <div className="linked-products-card" style={themeVars}>
      <div className="linked-products-grid">
        {loading ? (
          <div className="linked-products-state">
            <Spin />
          </div>
        ) : list.length === 0 ? (
          <div className="linked-products-state">No linked products for this company.</div>
        ) : list.map((product) => {
          const href = product.signin_url
            ? `${product.signin_url}${product.signin_url.includes("?") ? "&" : "?"}token=${encodeURIComponent(ssoToken)}`
            : null;
          const label = product.product_name || product.product_slug || "Product";
          const logo = getProductLogo(label);
          const key = product.product_slug || product.product_name || label;

          const cardContent = (
            <>
              <span className="linked-products-logo">
                {logo ? <img src={logo} alt="" /> : <AppstoreOutlined />}
              </span>
              <span className="linked-products-label" title={label}>{label}</span>
            </>
          );

          return href ? (
            <a
              key={key}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="linked-products-tile"
            >
              {cardContent}
            </a>
          ) : (
            <div key={key} className="linked-products-tile linked-products-tile--disabled">
              {cardContent}
            </div>
          );
        })}
      </div>
    </div>
  );

  // No connected products for this company → hide the launcher button entirely.
  if (list.length === 0) return null;

  return (
    <li className="gx-language">
      <Popover
        placement="bottomRight"
        trigger="click"
        content={content}
        title={<span className="linked-products-title" style={themeVars}>Connected products</span>}
        onOpenChange={handleOpenChange}
        overlayClassName="linked-products-popover"
        overlayInnerStyle={{ background: theme.popoverBg, border: theme.popoverBorder }}
      >
        <span className="linked-products-trigger">
          <Tooltip title="Apps" placement="bottom" overlayClassName="app-topbar-tooltip">
            <span
              className="app-topbar__tile"
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === "Enter" && e.currentTarget.click()}
              aria-label="Connected products"
            >
              <AppstoreOutlined />
            </span>
          </Tooltip>
        </span>
      </Popover>
    </li>
  );
};

export default LinkedProductsLauncher;
